<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import CampaignsAPI from 'dashboard/api/campaigns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const campaign = ref(null);
const summary = ref(null);
const notReceived = ref([]);
const isLoading = ref(true);
const loadError = ref(false);

const fetchReport = async () => {
  isLoading.value = true;
  loadError.value = false;
  try {
    const [campaignResponse, reportResponse] = await Promise.all([
      CampaignsAPI.show(route.params.campaignId),
      CampaignsAPI.report(route.params.campaignId),
    ]);
    campaign.value = campaignResponse.data;
    summary.value = reportResponse.data.summary;
    notReceived.value = reportResponse.data.not_received;
  } catch (error) {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchReport);

const deliveryRatePercentLabel = computed(() => {
  const percent = summary.value
    ? Math.round(summary.value.delivery_rate * 100)
    : 0;
  return `${percent}%`;
});

const summaryCards = computed(() => {
  if (!summary.value) return [];
  return [
    {
      key: 'total',
      label: t('CAMPAIGN_REPORT.SUMMARY.TOTAL'),
      value: summary.value.total,
    },
    {
      key: 'dispatched',
      label: t('CAMPAIGN_REPORT.SUMMARY.DISPATCHED'),
      value: summary.value.dispatched,
    },
    {
      key: 'sent',
      label: t('CAMPAIGN_REPORT.SUMMARY.SENT'),
      value: summary.value.sent,
    },
    {
      key: 'delivered',
      label: t('CAMPAIGN_REPORT.SUMMARY.DELIVERED'),
      value: summary.value.delivered,
    },
    {
      key: 'read',
      label: t('CAMPAIGN_REPORT.SUMMARY.READ'),
      value: summary.value.read,
    },
    {
      key: 'failed',
      label: t('CAMPAIGN_REPORT.SUMMARY.FAILED'),
      value: summary.value.failed,
    },
    {
      key: 'ignored',
      label: t('CAMPAIGN_REPORT.SUMMARY.IGNORED_NO_PHONE'),
      value: summary.value.ignored_no_phone,
    },
  ];
});

const tableHeaders = computed(() => [
  t('CAMPAIGN_REPORT.NOT_RECEIVED.COLUMNS.CONTACT'),
  t('CAMPAIGN_REPORT.NOT_RECEIVED.COLUMNS.REASON'),
  '',
]);

const openConversation = entry => {
  if (!entry.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: entry.conversation_id },
  });
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="flex items-center w-full max-w-3xl mx-auto h-20">
        <span class="text-heading-1 text-n-slate-12">
          {{ campaign?.title || t('CAMPAIGN_REPORT.HEADER_TITLE') }}
        </span>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="grid w-full max-w-3xl gap-6 py-4 mx-auto">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div v-else-if="loadError" class="py-10 text-center text-n-slate-11">
          {{ t('CAMPAIGN_REPORT.LOAD_ERROR') }}
        </div>
        <template v-else>
          <div
            v-if="campaign?.dispatch_error"
            class="p-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
          >
            {{
              t('CAMPAIGN_REPORT.DISPATCH_ERROR_BANNER', {
                reason: campaign.dispatch_error,
              })
            }}
          </div>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div
              v-for="card in summaryCards"
              :key="card.key"
              class="grid gap-1 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <span class="text-2xl font-semibold text-n-slate-12">{{
                card.value
              }}</span>
              <span class="text-xs text-n-slate-11">{{ card.label }}</span>
            </div>
            <div
              class="grid gap-1 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <span class="text-2xl font-semibold text-n-slate-12">{{
                deliveryRatePercentLabel
              }}</span>
              <span class="text-xs text-n-slate-11">{{
                t('CAMPAIGN_REPORT.SUMMARY.DELIVERY_RATE')
              }}</span>
            </div>
          </div>

          <div class="grid gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN_REPORT.NOT_RECEIVED.TITLE') }}
            </span>
            <BaseTable
              :headers="tableHeaders"
              :items="notReceived"
              :no-data-message="t('CAMPAIGN_REPORT.NOT_RECEIVED.EMPTY_STATE')"
            >
              <template #row="{ items }">
                <BaseTableRow
                  v-for="(entry, index) in items"
                  :key="index"
                  :item="entry"
                  :class="{ 'cursor-pointer': entry.conversation_id }"
                  @click="openConversation(entry)"
                >
                  <template #default>
                    <BaseTableCell>
                      <span class="text-body-main text-n-slate-12">
                        {{ entry.contact.name }}
                      </span>
                    </BaseTableCell>
                    <BaseTableCell>
                      <span class="text-body-main text-n-slate-11">
                        {{ entry.reason }}
                      </span>
                    </BaseTableCell>
                    <BaseTableCell>
                      <span
                        v-if="entry.conversation_id"
                        class="text-xs text-n-slate-12"
                      >
                        {{
                          t('CAMPAIGN_REPORT.NOT_RECEIVED.OPEN_CONVERSATION')
                        }}
                      </span>
                    </BaseTableCell>
                  </template>
                </BaseTableRow>
              </template>
            </BaseTable>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>
